/**
 * Where Dhobara ships, in one place.
 *
 * Both the shipping address at checkout and the card's billing address
 * restrict their country dropdowns to this list. They were separate before
 * and only the shipping one was restricted, so a buyer could still pick any
 * country for billing — the kind of split that gets fixed once and then
 * drifts again the next time.
 *
 * Add 'CA' here when Canada launches; nothing else needs to change.
 */

export const SHIPPABLE_COUNTRY_CODES = ['US'];

/**
 * Narrow a getCountryCodes(locale) list to the countries we ship to,
 * preserving the localised names that list carries.
 */
export const filterToShippableCountries = countryCodes =>
  (countryCodes || []).filter(c => SHIPPABLE_COUNTRY_CODES.includes(c.code));

/**
 * Resolve whatever we have stored for a country into an ISO-3166 code.
 *
 * The Contact Details page saves the country as a DISPLAY NAME — profiles
 * hold "United States", not "US". Checkout prefills the shipping form from
 * that, and the country <select>'s option values are codes, so the prefilled
 * value matched no option. Stripe then rejected the payment outright:
 * "Country 'United States' is unknown. Try using a 2-character alphanumeric
 * country code instead."
 *
 * Accepts a code (returned as-is, upper-cased) or a name in any of the
 * locales countryCodes.js carries. Returns '' when it cannot tell, so the
 * caller can fall back rather than send Stripe something it will refuse.
 */
export const toCountryCode = (value, countryCodes) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();

  const list = countryCodes || [];
  const lower = trimmed.toLowerCase();
  const hit = list.find(c =>
    Object.keys(c).some(k => k !== 'code' && String(c[k]).toLowerCase() === lower)
  );
  return hit ? hit.code : '';
};

/** The country to assume when we ship to exactly one. */
export const defaultShippableCountryCode = () =>
  SHIPPABLE_COUNTRY_CODES.length === 1 ? SHIPPABLE_COUNTRY_CODES[0] : '';
